import { useParams } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { FormattedMessage, useIntl } from "react-intl";
import { useContainerTabs } from "../../../contexts/containerTabsContext";
import { DictionaryString } from "../../../utils/types/generalTypes";
import { SunkhronosCompany, SunkhronosCompanyUsage as SunkhronosCompanyUsageType } from "../../../utils/types/applications/sunkhronosTypes";
import Network from "../../../utils/Network";
import { ConvertNetworkSunkhronosCompany, ConvertNetworkSunkhronosCompanyUsage } from "../../../utils/converters";
import { showNotification } from "../../../utils/utils";
import { TabItem } from "../../common/ContainerTabs/types";
import FAIcon from "../../common/FAIcon/FAIcon";
import ContainerTabs from "../../common/ContainerTabs/ContainerTabs";
import SunkhronosCompanyUsage from "./tabs/SunkhronosCompanyUsage";
import { useViewAddons } from "../../common/Layout/ViewAddons/ViewAddons";
import { Space } from "antd";
import BackArrow from "../../common/BackArrow/BackArrow";
import SkeletonInput from "antd/es/skeleton/Input";
import { SidebarEnum } from "../../common/Layout/ViewAddons/ViewAddonsRenderer";

const TABS = {
    BILLING: 'billing',
    CONFIGURATION: 'configuration',
    USAGE: 'usage'
};

const SunkhronosCompanyManagement = () => {
    const intl = useIntl();
    const viewAddons = useViewAddons();

    const { tab, companyId } = useParams();
    const { tabExtra, tabSidebars } = useContainerTabs();

    const [company, setCompany] = useState<SunkhronosCompany | undefined>(undefined);
    const [companyLoading, setCompanyLoading] = useState(false);

    const [usage, setUsage] = useState<DictionaryString<SunkhronosCompanyUsageType>>({});
    const [usageLoading, setUsageLoading] = useState(false);
    const [startDate, setStartDate] = useState(dayjs().startOf('year'));
    const [endDate, setEndDate] = useState(dayjs().endOf('year'));

    //#region Network
    const handleRefreshCompanyUsage = useCallback(() => {
        if (companyId !== undefined) {
            setUsageLoading(true);
            Network.fetchSunkhronosCompanyUsage(Number.parseInt(companyId), startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD')).then(
                (res) => {
                    setUsage(ConvertNetworkSunkhronosCompanyUsage(res.data));
                    setUsageLoading(false);
                },
                () => {
                    showNotification(intl.formatMessage({ defaultMessage: 'An error occurred while retrieving the company usage' }), 'error');
                    setUsageLoading(false);
                }
            );
        }
    }, [endDate, companyId, intl, startDate]);

    const handleRefreshCompany = useCallback(() => {
        if (companyId !== undefined) {
            setCompanyLoading(true);
            Network.fetchSunkhronosCompanyDetails(Number.parseInt(companyId)).then(
                (res) => {
                    setCompany(ConvertNetworkSunkhronosCompany(res.data));
                    setCompanyLoading(false);
                },
                () => {
                    showNotification(intl.formatMessage({ defaultMessage: 'An error occurred while retrieving the company' }), 'error');
                    setCompanyLoading(false);
                }
            );
        }
    }, [companyId, intl]);

    useEffect(() => {
        handleRefreshCompanyUsage();
        handleRefreshCompany();
    }, [endDate, companyId, handleRefreshCompany, handleRefreshCompanyUsage, startDate]);
    //#endregion

    //#region ViewAddons
    const updateViewAddonsContent = useCallback(() => {
        viewAddons.setContent(
            <Space>
                <BackArrow url='sunkhronos/companies' />
                <span
                    title={`${company?.name}`}
                    style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: '100%', textTransform: 'uppercase', fontStyle: 'italic' }}
                >
                    <span>{'Sunkhronos / '}</span>
                    {
                        companyLoading ?
                            <SkeletonInput active style={{ height: 15 }} />
                            :
                            company?.name
                    }
                </span>
            </Space>
        );
    }, [company, companyLoading, viewAddons]);

    useEffect(() => {
        updateViewAddonsContent();
    }, [updateViewAddonsContent]);

    useEffect(() => {
        viewAddons.setSidebarType(SidebarEnum.SETTINGS);
    }, [viewAddons]);
    //#endregion

    const tabItems = useMemo((): TabItem[] => {
        return [
            {
                key: TABS.USAGE,
                label: <span><FAIcon prefix='fad' name='chart-line' /> {<FormattedMessage
                    defaultMessage={'Usage'}
                />}</span>,
                children: <SunkhronosCompanyUsage
                    company={company}
                    usage={usage}
                    loading={usageLoading}
                    endDate={endDate}
                    setEndDate={setEndDate}
                    setStartDate={setStartDate}
                    startDate={startDate}
                    refreshUsage={handleRefreshCompanyUsage}
                    setCompany={setCompany}
                />
            },
        ];
    }, [company, endDate, handleRefreshCompanyUsage, startDate, usage, usageLoading]);

    return (
        <ContainerTabs
            baseUrl={`sunkhronos/companies/${companyId}`}
            tabItems={tabItems}
            tabExtra={tabExtra.find(item => item.key === tab)?.content}
            tabSidebars={tabSidebars.find(item => item.key === tab)?.content}
        />
    );
};

export default SunkhronosCompanyManagement;